import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { AdminSignIn, AdminNav } from "@/components/admin/AdminShell";
import { getReliabilityReport } from "@/lib/reliability.functions";
import { getReconciliation, runReconciliationNow } from "@/lib/reconciliation.functions";
import type { ReconReport } from "@/lib/reconciliation.server";
import { toCsv, downloadCsv } from "@/lib/csv-export";
import type { ReliabilityReport, CountRow } from "@/lib/reliability.server";

export const Route = createFileRoute("/admin/reliability")({
  component: AdminReliabilityPage,
  head: () => ({
    meta: [
      { title: "Reliability · SFN internal" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AdminReliabilityPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<ReliabilityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useServerFn(getReliabilityReport);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setAuthed(Boolean(session)),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport((await run({ data: { days } })) as ReliabilityReport);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Report failed.");
    } finally {
      setLoading(false);
    }
  }, [run, days]);

  useEffect(() => {
    if (authed) void refresh();
  }, [authed, refresh]);

  if (authed === null) return null;

  return (
    <main className="mx-auto max-w-5xl px-5 pb-24 pt-16">
      <h1 className="text-2xl font-bold tracking-tight">Reliability & conversion</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Wallet rejections, RPC failures, verified receipts and the deploy canary.
      </p>

      {!authed ? (
        <AdminSignIn note="Sign in with your internal admin account." />
      ) : (
        <>
          <AdminNav current="reliability" />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  days === d ? "bg-white/[0.14]" : "bg-white/[0.05] hover:bg-white/[0.1]"
                }`}
              >
                Last {d}d
              </button>
            ))}
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              className="rounded-full bg-white/[0.08] px-4 py-1.5 text-xs font-semibold transition hover:bg-white/[0.14] disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {error && <p className="mt-4 text-sm text-red-300">{error}</p>}

          {report && (
            <>
              <section className="mt-6 rounded-2xl glass p-4" aria-live="polite">
                <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Live alerts · last hour
                </h2>
                {report.alerts.length === 0 ? (
                  <p className="mt-2 text-sm text-emerald-300">
                    All clear — no rejection spikes, RPC degradation or funnel drop-offs.
                  </p>
                ) : (
                  <ul className="mt-2 grid gap-2">
                    {report.alerts.map((a) => (
                      <li
                        key={a.key}
                        className={`rounded-xl border px-3 py-2 text-sm ${
                          a.severity === "critical"
                            ? "border-red-400/40 text-red-300"
                            : "border-amber-400/40 text-amber-300"
                        }`}
                      >
                        <span className="font-semibold">{a.key}</span> — {a.message}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="mt-6 rounded-2xl glass p-4">
                <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Swap route health · last {report.days}d
                </h2>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  {[
                    { label: "Jupiter quote failures", value: report.ops.priceFeedFailures },
                    { label: "Slow price quotes", value: report.ops.priceFeedSlow },
                    { label: "Slow / failed RPC calls", value: report.ops.rpcSlow },
                    {
                      label: "RPC latency p95",
                      value: report.ops.rpcLatencyP95 === null ? "—" : `${report.ops.rpcLatencyP95} ms`,
                    },
                    { label: "Settlements rejected", value: report.ops.settlementRejected },
                    { label: "Settlement warnings", value: report.ops.settlementWarnings },
                  ].map((m) => (
                    <div key={m.label} className="rounded-xl bg-white/[0.04] px-3 py-2">
                      <dt className="text-[11px] text-muted-foreground">{m.label}</dt>
                      <dd className="mt-1 font-mono text-base">{m.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <div
                className={`mt-6 rounded-2xl glass px-4 py-3 text-sm ${
                  report.canary.lastStatus === "fail" ? "text-red-300" : "text-muted-foreground"
                }`}
                role="status"
              >
                <span className="font-semibold text-foreground">Deploy canary: </span>
                {report.canary.lastStatus === "unknown"
                  ? "never run"
                  : `${report.canary.lastStatus.toUpperCase()} · ${
                      report.canary.lastRunAt
                        ? new Date(report.canary.lastRunAt).toLocaleString()
                        : "—"
                    } · ${report.canary.failures24h} failure(s) in 24h`}
                {report.canary.lastDetail && (
                  <span className="block break-words text-xs">{report.canary.lastDetail}</span>
                )}
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Stat label="Swaps submitted" value={report.totals.swapSubmitted} />
                <Stat label="Confirmed" value={report.totals.swapConfirmed} accent="ok" />
                <Stat
                  label="Conversion"
                  value={`${report.conversionPct}%`}
                  accent={report.conversionPct < 50 ? "warn" : "ok"}
                />
                <Stat
                  label="Wallet rejections"
                  value={report.totals.walletRejected}
                  accent={report.rejectionPct > 30 ? "fail" : "warn"}
                />
                <Stat label="Swap failures" value={report.totals.swapFailed} accent="fail" />
                <Stat
                  label="RPC failures"
                  value={report.totals.rpcFailures}
                  accent={report.totals.rpcFailures > 0 ? "warn" : "ok"}
                />
                <Stat label="Receipts viewed" value={report.totals.receiptViewed} />
                <Stat
                  label="On-chain verified receipts"
                  value={report.totals.receiptVerified}
                  accent="ok"
                />
              </div>

              <div className="mt-8 grid gap-4 md:grid-cols-2">
                <Breakdown title="RPC failures by code" rows={report.rpcByCode} />
                <Breakdown title="RPC failures by method" rows={report.rpcByMethod} />
                <Breakdown title="Swap failures by code" rows={report.failuresByCode} />
                <Breakdown title="Receipts by attribution source" rows={report.receiptsBySource} />
              </div>

              <h2 className="mt-10 text-lg font-semibold tracking-tight">Daily trend</h2>
              <div className="mt-3 overflow-x-auto rounded-2xl glass">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">Daily swap and reliability counts</caption>
                  <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-2">
                        Day
                      </th>
                      <th scope="col" className="px-4 py-2">
                        Submitted
                      </th>
                      <th scope="col" className="px-4 py-2">
                        Confirmed
                      </th>
                      <th scope="col" className="px-4 py-2">
                        Rejected
                      </th>
                      <th scope="col" className="px-4 py-2">
                        RPC failures
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.daily.length === 0 ? (
                      <tr>
                        <td className="px-4 py-3 text-muted-foreground" colSpan={5}>
                          No activity in this window.
                        </td>
                      </tr>
                    ) : (
                      report.daily.map((d) => (
                        <tr key={d.day} className="border-t border-white/[0.06]">
                          <td className="px-4 py-2">{d.day}</td>
                          <td className="px-4 py-2">{d.submitted}</td>
                          <td className="px-4 py-2">{d.confirmed}</td>
                          <td className="px-4 py-2">{d.rejected}</td>
                          <td className="px-4 py-2">{d.rpc}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <p className="mt-6 text-xs text-muted-foreground">
                Generated {new Date(report.generatedAt).toLocaleString()} · window {report.days}d
              </p>

              <ReconciliationPanel />
            </>
          )}
        </>
      )}
    </main>
  );
}

const ACCENT: Record<string, string> = {
  ok: "text-emerald-300",
  warn: "text-amber-300",
  fail: "text-red-300",
};

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: "ok" | "warn" | "fail";
}) {
  return (
    <div className="rounded-2xl glass p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${accent ? ACCENT[accent] : ""}`}>{value}</p>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: CountRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="rounded-2xl glass p-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No events recorded.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r.key}>
              <div className="flex items-center justify-between text-xs">
                <span className="truncate">{r.key}</span>
                <span className="text-muted-foreground">{r.count}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-white/[0.06]">
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-saffron to-coral"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


/** Daily on-chain reconciliation: ledger rows vs finalized Solana signatures. */
function ReconciliationPanel() {
  const load = useServerFn(getReconciliation);
  const rerun = useServerFn(runReconciliationNow);
  const [report, setReport] = useState<ReconReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    load({ data: {} } as never)
      .then((r) => setReport((r as { report: ReconReport | null }).report))
      .catch(() => setErr("Could not load the latest reconciliation run."));
  }, [load]);

  const runNow = () => {
    setBusy(true);
    setErr(null);
    rerun({ data: { days: 30 } })
      .then((r) => setReport((r as { report: ReconReport }).report))
      .catch(() => setErr("Reconciliation run failed."))
      .finally(() => setBusy(false));
  };

  const auditRows =
    report && showAll && report.rows && report.rows.length > 0 ? report.rows : (report?.findings ?? []);

  const exportCsv = () => {
    if (!report) return;
    const csv = toCsv([
      [
        "sale_id",
        "created_at",
        "token",
        "signature",
        "wallet",
        "usd",
        "status",
        "chain_status",
        "reason",
        "normalized",
      ],
      ...auditRows.map((f) => [
        f.saleId,
        f.createdAt,
        f.token,
        f.signature,
        f.walletMasked,
        f.usd,
        f.status,
        f.chainStatus,
        f.reason ?? "",
        f.normalized ? "yes" : "no",
      ]),
    ]);
    downloadCsv(`sfn-reconciliation-${report.runAt.slice(0, 10)}.csv`, csv);
  };



  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">On-chain reconciliation</h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runNow}
            disabled={busy}
            className="rounded-full bg-white/[0.08] px-4 py-1.5 text-xs font-semibold transition hover:bg-white/[0.14] disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Run now"}
          </button>
          <button
            type="button"
            aria-label="Download the reconciliation report as a CSV file"
            onClick={exportCsv}
            disabled={!report}
            className="rounded-full bg-white/[0.08] px-4 py-1.5 text-xs font-semibold transition hover:bg-white/[0.14] disabled:opacity-50"
          >
            Download CSV
          </button>
        </div>
      </div>

      {err && <p className="mt-3 text-sm text-red-300">{err}</p>}
      {!report && !err && (
        <p className="mt-3 text-sm text-muted-foreground">No reconciliation run recorded yet.</p>
      )}

      {report && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Checked" value={report.checked} />
            <Stat label="Matched" value={report.matched} accent="ok" />
            <Stat
              label="Mismatched"
              value={report.mismatched}
              accent={report.mismatched > 0 ? "fail" : "ok"}
            />
            <Stat
              label="Missing on-chain"
              value={report.missing}
              accent={report.missing > 0 ? "fail" : "ok"}
            />
            <Stat
              label="Unreachable"
              value={report.unreachable}
              accent={report.unreachable > 0 ? "warn" : "ok"}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {(["all", "issues"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={showAll === (mode === "all")}
                onClick={() => setShowAll(mode === "all")}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  showAll === (mode === "all")
                    ? "bg-white/[0.14]"
                    : "bg-white/[0.05] hover:bg-white/[0.1]"
                }`}
              >
                {mode === "all" ? "All rows (audit log)" : "Discrepancies only"}
              </button>
            ))}
            {(report.normalizedCount ?? 0) > 0 && (
              <span className="text-xs text-amber-300">
                {report.normalizedCount} signature(s) base58-normalized this run
              </span>
            )}
          </div>

          <div className="mt-3 overflow-x-auto rounded-2xl glass">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Row-by-row reconciliation audit log with status and reason
              </caption>
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th scope="col" className="px-4 py-2">Sale</th>
                  <th scope="col" className="px-4 py-2">Date</th>
                  <th scope="col" className="px-4 py-2">Token</th>
                  <th scope="col" className="px-4 py-2">Wallet</th>
                  <th scope="col" className="px-4 py-2">Signature</th>
                  <th scope="col" className="px-4 py-2">Status</th>
                  <th scope="col" className="px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-3 text-emerald-300" colSpan={7}>
                      Every ledger row matched a finalized on-chain signature.
                    </td>
                  </tr>
                ) : (
                  auditRows.map((f) => (
                    <tr key={f.saleId} className="border-t border-white/[0.06]">
                      <td className="px-4 py-2">#{f.saleId}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {new Date(f.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">{f.token}</td>
                      <td className="px-4 py-2 font-mono text-xs">{f.walletMasked}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {f.signature.slice(0, 8)}…{f.signature.slice(-6)}
                      </td>
                      <td
                        className={`px-4 py-2 font-semibold ${
                          f.status === "matched"
                            ? "text-emerald-300"
                            : f.status === "unreachable"
                              ? "text-amber-300"
                              : "text-red-300"
                        }`}
                      >
                        {f.status}
                        {f.normalized ? " ·norm" : ""}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{f.reason ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Last run {new Date(report.runAt).toLocaleString()} · {auditRows.length} row(s) shown
          </p>

        </>
      )}
    </section>
  );
}
